import type { FieldCatalogueEntry, FieldEditRejection, RequestFieldView } from "@procuremate/shared";
import { FIELD_EDIT_REJECTIONS } from "@procuremate/shared";
import { clsx } from "clsx";
import { type FormEvent, type KeyboardEvent, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import type { Language } from "@/i18n";
import { ApiRequestError } from "@/lib/api-client";
import { useEditField } from "./api";
import { editorText, valueToSend } from "./field-format";

const CONTROL = "w-full min-w-0 rounded-md border bg-surface px-3 py-2 text-base text-fg";

/** Why the API refused an edit, as a key of requests.panel.editErrors. */
function errorKey(error: unknown): string {
  if (error instanceof ApiRequestError) {
    if (error.status === 409) return "busy";
    const reason = (error.details as { reason?: string } | undefined)?.reason;
    if (error.status === 400 && FIELD_EDIT_REJECTIONS.includes(reason as FieldEditRejection)) return reason as string;
    if (error.status === 400) return "invalid_value";
  }
  return "generic";
}

/**
 * Inline editor for one element of the live panel (ADR-0014), with a control for its type.
 * Enter in a one-line control saves, Escape cancels; the parent puts focus back on its button.
 */
export function FieldEditor({
  requestId,
  entry,
  field,
  labelId,
  onClose,
}: {
  requestId: string;
  entry: FieldCatalogueEntry;
  field: RequestFieldView | undefined;
  /** Id of the visible label that names the control. */
  labelId: string;
  onClose: (saved: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const language = i18n.language as Language;
  const edit = useEditField(requestId);
  const errorId = useId();
  const hintId = useId();
  const firstControl = useRef<HTMLElement | null>(null);
  const captured = field?.status === "CAPTURED" ? field.value : undefined;
  const [text, setText] = useState(editorText(captured));
  const [choice, setChoice] = useState(typeof captured === "string" ? captured : "");
  const [yesNo, setYesNo] = useState<boolean | null>(typeof captured === "boolean" ? captured : null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => firstControl.current?.focus(), []);

  function submit(event?: FormEvent) {
    event?.preventDefault();
    if (edit.isPending) return;
    const value =
      entry.type === "yes_no" ? yesNo : entry.type === "choice" ? choice || null : valueToSend(entry.type, text);
    if (value === null) {
      setError("empty");
      return;
    }
    setError(null);
    edit.mutate(
      { key: entry.key, value },
      { onSuccess: () => onClose(true), onError: (err) => setError(errorKey(err)) },
    );
  }

  function onKeyDown(event: KeyboardEvent) {
    if (event.key === "Escape") {
      event.preventDefault();
      onClose(false);
    }
  }

  const describedBy = clsx(error && errorId, entry.type === "list" && hintId) || undefined;
  const controlClass = clsx(CONTROL, error ? "border-danger" : "border-border");
  const clearError = () => error && setError(null);

  return (
    <form className="mt-2 flex flex-col gap-2" onSubmit={submit} onKeyDown={onKeyDown} noValidate>
      {entry.type === "choice" ? (
        <select
          ref={(el) => {
            firstControl.current = el;
          }}
          aria-labelledby={labelId}
          aria-describedby={describedBy}
          aria-invalid={error ? true : undefined}
          className={controlClass}
          value={choice}
          onChange={(e) => {
            setChoice(e.target.value);
            clearError();
          }}
        >
          <option value="">{t("requests.panel.chooseOption")}</option>
          {entry.options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.labels[language]}
            </option>
          ))}
        </select>
      ) : entry.type === "yes_no" ? (
        <fieldset aria-labelledby={labelId} aria-describedby={describedBy} className="flex gap-4">
          {[true, false].map((option, index) => (
            <label key={String(option)} className="inline-flex items-center gap-2 text-base">
              <input
                ref={
                  index === 0
                    ? (el) => {
                        firstControl.current = el;
                      }
                    : undefined
                }
                type="radio"
                name={`${entry.key}-yes-no`}
                className="h-4 w-4 accent-primary"
                checked={yesNo === option}
                onChange={() => {
                  setYesNo(option);
                  clearError();
                }}
              />
              {option ? t("requests.panel.yes") : t("requests.panel.no")}
            </label>
          ))}
        </fieldset>
      ) : entry.type === "money" || entry.type === "duration" ? (
        <input
          ref={(el) => {
            firstControl.current = el;
          }}
          aria-labelledby={labelId}
          aria-describedby={describedBy}
          aria-invalid={error ? true : undefined}
          className={clsx(controlClass, "h-11")}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            clearError();
          }}
        />
      ) : (
        <textarea
          ref={(el) => {
            firstControl.current = el;
          }}
          aria-labelledby={labelId}
          aria-describedby={describedBy}
          aria-invalid={error ? true : undefined}
          rows={entry.type === "list" ? 4 : 3}
          className={clsx(controlClass, "resize-y")}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            clearError();
          }}
        />
      )}
      {entry.type === "list" ? (
        <p id={hintId} className="text-xs text-fg-muted">
          {t("requests.panel.listHint")}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} role="alert" className="text-sm text-danger">
          {t(`requests.panel.editErrors.${error}`)}
        </p>
      ) : null}
      <div className="flex gap-2">
        {/* Not `disabled` while saving: that would drop keyboard focus to the page. */}
        <Button type="submit" className="h-9 px-3" aria-disabled={edit.isPending || undefined}>
          {edit.isPending ? t("requests.panel.saving") : t("requests.panel.save")}
        </Button>
        <Button variant="secondary" className="h-9 px-3" onClick={() => onClose(false)}>
          {t("requests.panel.cancel")}
        </Button>
      </div>
    </form>
  );
}
